import axios from "axios";
import "katex/dist/katex.min.css";
import { useEffect, useState } from "react";
import { Breadcrumb, Col, Container, Nav, Row } from "react-bootstrap";
import ReactMarkdown from "react-markdown";
import { Link, useParams } from "react-router-dom";
import rehypeKatex from "rehype-katex";
import rehypeSlug from "rehype-slug";
import remarkMath from "remark-math";
import "../../css/Blog.css";

const BlogPost = () => {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [headings, setHeadings] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const { slug } = useParams();

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return "";
    if (imageUrl.startsWith("http")) return imageUrl;
    return `${process.env.REACT_APP_BASE_API_URL}${imageUrl}`;
  };

  // Custom renderer for images in ReactMarkdown
  const renderers = {
    img: ({ src, alt }) => (
      <img
        src={getImageUrl(src)}
        alt={alt}
        className="img-fluid clickable-image"
        onClick={() => setSelectedImage(getImageUrl(src))}
      />
    ),
  };

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_API_URL}/api/blogs/blog-${slug}/`,
        );
        setPost(response.data);

        // Extract headings from markdown content
        const lines = response.data.content.split("\n");
        const extractedHeadings = lines
          .filter((line) => line.startsWith("#"))
          .map((line) => {
            const level = line.match(/^#+/)[0].length;
            const text = line.replace(/^#+\s/, "").trim();
            const id = text
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/(^-|-$)+/g, "");
            return { level, text, id };
          });
        setHeadings(extractedHeadings);

        setLoading(false);
      } catch (error) {
        console.error("Error fetching post:", error);
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  // Handle scroll spy for ToC
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: "0% 0% -80% 0%" },
    );

    const headingElements = document.querySelectorAll("h1, h2, h3, h4, h5, h6");
    headingElements.forEach((element) => observer.observe(element));

    // Also observe the top of the page
    const topElement = document.getElementById("top");
    if (topElement) observer.observe(topElement);

    return () => {
      headingElements.forEach((element) => observer.unobserve(element));
      if (topElement) observer.unobserve(topElement);
    };
  }, [loading, post]); // Re-run when post loads

  // Auto-scroll ToC to active item
  useEffect(() => {
    if (activeId === "top") {
      const sidebar = document.querySelector(".toc-sidebar");
      if (sidebar) sidebar.scrollTop = 0;
      return;
    }

    if (activeId) {
      const activeElement =
        document.querySelector(`.toc-link[href="#${activeId}"]`) ||
        document.querySelector(`.toc-link.active`);
      if (activeElement) {
        activeElement.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }
  }, [activeId]);

  if (loading)
    return (
      <Container className="mt-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </Container>
    );
  if (!post)
    return (
      <Container className="mt-5">
        <div>Post not found</div>
      </Container>
    );

  return (
    <>
      <Container className="mt-4 mb-5 blog-post-container">
        <Row>
          {/* Sidebar / ToC Column */}
          <Col md={3} className="d-none d-md-block">
            <div className="toc-sidebar">
              <h5 className="mb-3">Table of Contents</h5>
              <Nav className="flex-column">
                {headings.map((heading) => (
                  <Nav.Link
                    key={heading.id}
                    onClick={(e) => {
                      e.preventDefault();
                      document
                        .getElementById(heading.id)
                        ?.scrollIntoView({ behavior: "smooth" });
                      // Optionally update URL hash without triggering router if needed,
                      // but for HashRouter it's safer to just scroll.
                    }}
                    className={`toc-link level-${heading.level} ${activeId === heading.id ? "active" : ""}`}
                    style={{ cursor: "pointer" }}
                    href={`#${heading.id}`}
                  >
                    {heading.text}
                  </Nav.Link>
                ))}
                <Nav.Link
                  onClick={(e) => {
                    e.preventDefault();
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className="toc-link back-to-top mt-2"
                  style={{ cursor: "pointer" }}
                >
                  ↑ Scroll to Top
                </Nav.Link>
              </Nav>
            </div>
          </Col>

          {/* Main Content Column */}
          <Col md={7}>
            <div id="top"></div>
            <Breadcrumb>
              <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/blogs" }}>
                Blogs
              </Breadcrumb.Item>
              <Breadcrumb.Item
                linkAs={Link}
                linkProps={{ to: `/blogs?category=${post.category_slug}` }}
              >
                {post.category_name}
              </Breadcrumb.Item>
              <Breadcrumb.Item active>{post.title}</Breadcrumb.Item>
            </Breadcrumb>

            {post.featured_image && (
              <div className="featured-image-wrapper mb-4">
                <img
                  src={post.featured_image}
                  alt={post.title}
                  className="img-fluid featured-image"
                />
              </div>
            )}

            <h1 className="blog-post-title">{post.title}</h1>
            <div className="blog-meta mb-5">
              <span>{new Date(post.created_at).toLocaleDateString()}</span>
              <span className="mx-2">•</span>
              <span>{post.read_time} min read</span>
            </div>

            <div className="blog-content">
              <ReactMarkdown
                remarkPlugins={[remarkMath]}
                rehypePlugins={[rehypeKatex, rehypeSlug]}
                components={renderers}
              >
                {post.content}
              </ReactMarkdown>
            </div>
          </Col>
          <Col md={2}></Col>
        </Row>
      </Container>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="lightbox-overlay"
          onClick={() => setSelectedImage(null)}
        >
          <div className="lightbox-content">
            <img
              src={selectedImage}
              alt="Fullscreen view"
              className="lightbox-image"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default BlogPost;
